import {
  Component,
  OnDestroy,
  inject,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, startWith } from 'rxjs/operators';

import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { BusinessPartnerNavigationService } from 'app/shared/services/business-partner-navigation.service';

import { AssignTerritoryModalService } from '../assign-territory-modal.service';
import {
  AddressRowView,
  createAddressRowView,
} from '../interfaces/assign-territory-modal.interface';

@Component({
  selector: 'app-address-step',
  templateUrl: './address-step.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatCheckboxModule,
    MatChipsModule,
    MatTooltipModule,
    MatMenuModule,
    FormSectionComponent,
  ],
})
export class AddressStepComponent implements OnDestroy {
  private menuCloseTimeout: any;
  private readonly MAX_VISIBLE = 3;

  // Inject services
  readonly modalService = inject(AssignTerritoryModalService);
  private readonly bpNavigationService = inject(BusinessPartnerNavigationService);

  // Search - convert FormControl to signal
  addressSearchCtrl = new FormControl('');
  private readonly searchTerm = toSignal(
    this.addressSearchCtrl.valueChanges.pipe(
      debounceTime(200),
      startWith('')
    ),
    { initialValue: '' }
  );

  // Table
  addressesDataSource = new MatTableDataSource<AddressRowView>([]);
  displayedColumns = ['select', 'name', 'address', 'routesCount'];

  // Computed: table data that automatically updates when addresses or selection changes
  readonly tableData = computed(() => {
    const addresses = this.modalService.clientAddresses();
    const term = (this.searchTerm() || '').toLowerCase().trim();
    const isSelected = this.modalService.isAddressSelectedFn();

    let filtered = addresses;
    if (term) {
      filtered = addresses.filter(addr =>
        addr.name?.toLowerCase().includes(term) ||
        addr.address?.toLowerCase().includes(term)
      );
    }

    return filtered.map(addr =>
      createAddressRowView(
        addr,
        isSelected(addr._id?.toString() || ''),
        this.MAX_VISIBLE
      )
    );
  });

  // Effect: automatically sync MatTableDataSource when tableData changes
  private tableEffect = effect(() => {
    this.addressesDataSource.data = this.tableData();
  });

  // Computed: check if all addresses are selected
  readonly isAllSelected = computed(() => {
    const data = this.tableData();
    return data.length > 0 && data.every(row => row.isSelected);
  });

  // Computed: check if some but not all addresses are selected (for indeterminate state)
  readonly isIndeterminate = computed(() => {
    const data = this.tableData();
    const selectedCount = data.filter(r => r.isSelected).length;
    return selectedCount > 0 && selectedCount < data.length;
  });

  ngOnDestroy(): void {
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
    }
  }

  /**
   * Toggle row selection - simplified, no manual sync needed
   */
  toggleRow(row: AddressRowView): void {
    this.modalService.toggleAddress(row);
    // tableData computed recalculates automatically
  }

  /**
   * Toggle all rows - simplified
   */
  masterToggle(): void {
    if (this.isAllSelected()) {
      this.modalService.deselectAllAddresses(this.tableData());
    } else {
      this.modalService.selectAllAddresses(this.tableData());
    }
    // tableData computed recalculates automatically
  }

  /**
   * Remove address from selection (from chip)
   */
  removeFromSelection(address: AddressRowView): void {
    this.modalService.removeAddress(address);
    // tableData computed recalculates automatically
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.addressSearchCtrl.setValue('');
  }

  /**
   * Close menu with delay for hover behavior
   */
  closeMenuWithDelay(trigger: MatMenuTrigger): void {
    this.menuCloseTimeout = setTimeout(() => {
      trigger.closeMenu();
    }, 100);
  }

  /**
   * Cancel menu close (when mouse enters popover)
   */
  cancelMenuClose(): void {
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
      this.menuCloseTimeout = null;
    }
  }

  /**
   * Navigate to client detail
   */
  navigateToClient(): void {
    const bp = this.modalService.businessPartner();
    if (!bp) return;

    this.bpNavigationService.navigateToBusinessPartnerDetail(
      {
        id: bp._id?.toString(),
        code: bp.externalKeys?.code,
        name: bp.name,
        externalKeys: bp.externalKeys,
      },
      {},
      true
    );
  }
}
